import type {
  Funder,
  Recommender,
  RoundAggregate,
} from "@engine/round/aggregate";
import type Matrix from "algorithm/matrix";
import {
  getFundableProposalIds,
  type RecommenderLayerInput,
} from "./recommenderPreferences";

/** A real recommender allocates one pool. Funder restrictions constrain who
 * can pay its grants, rather than creating separate preference profiles. */
export type RecommenderBudgetPortion = {
  id: string;
  recommender: Recommender;
  funderIds: string[];
  budget: number;
  standardBudget: number;
  funding?: {
    funderId: string;
    budget: number;
    standardBudget: number;
    excludedProposalIds: string[];
  }[];
};

export type RecommenderBudgetPortionSet = {
  portions: RecommenderBudgetPortion[];
};

/** Carry the separately shared standard and matching inflows into one pool. */
export function getBudgetPortionsFromFunderAccounts(
  funderAccounts: Matrix<"funder", "recommender+hold">,
  funders: Funder[],
  recommenderLayerInput: RecommenderLayerInput,
  standardFunderAccounts: Matrix<"funder", "recommender+hold">,
): RecommenderBudgetPortionSet {
  return {
    portions: recommenderLayerInput.orderedRecommenders.map((recommender) => {
      const col = recommenderLayerInput.recommenderIndicesMap[recommender.id]!;
      const funding = funders.map((funder, f) => {
        const budget = funderAccounts.get(f, col);
        return {
          funderId: funder.id,
          budget,
          standardBudget: standardFunderAccounts.get(f, col),
          excludedProposalIds: funder.excludedProposalIds ?? [],
        };
      });
      return {
        id: recommender.id,
        recommender,
        funderIds: funders.map((f) => f.id),
        budget: funding.reduce((total, f) => total + f.budget, 0),
        standardBudget: funding.reduce(
          (total, f) => total + f.standardBudget,
          0,
        ),
        funding,
      };
    }),
  };
}

/** Funding Flow and Decide supply the same funder inflows for hypothetical
 * budgets. Keep those inflows attached to a single pool for feasibility checks. */
export function getBudgetPortionsFromFunderAllocationSplit(
  round: RoundAggregate,
  recommenderLayerInput: RecommenderLayerInput,
  funderAllocationsByRecommender: {
    [recommenderId: string]: { [funderId: string]: number };
  },
  excludedByFunder: { [funderId: string]: string[] },
  recommenderBudgets: { [recommenderId: string]: number },
  standardRecommenderBudgets: { [recommenderId: string]: number },
  standardFunderAllocationsByRecommender: {
    [recommenderId: string]: { [funderId: string]: number };
  },
): RecommenderBudgetPortionSet {
  const result = getUnsplitBudgetPortions(
    recommenderLayerInput,
    recommenderBudgets,
    standardRecommenderBudgets,
  );

  for (const portion of result.portions) {
    const split = funderAllocationsByRecommender[portion.id] ?? {};
    const fundable = getFundableProposalIds(round, portion.recommender, {});
    portion.funding = Object.entries(split).map(([funderId, budget]) => {
      return {
        funderId,
        budget,
        standardBudget:
          standardFunderAllocationsByRecommender[portion.id]?.[funderId] ?? 0,
        excludedProposalIds: (excludedByFunder[funderId] ?? []).filter((id) =>
          fundable.has(id),
        ),
      };
    });
    portion.funderIds = Object.keys(split);
    // Source capacities come from the same allocation as the combined inflow.
    // Independent standard/matching sharing cannot be reconstructed from ratios.
    portion.standardBudget = portion.funding.reduce(
      (total, f) => total + f.standardBudget,
      0,
    );
  }

  return result;
}

/** Fast path for projections that do not need funder attribution. */
export function getUnsplitBudgetPortions(
  recommenderLayerInput: RecommenderLayerInput,
  recommenderBudgets: { [recommenderId: string]: number },
  standardRecommenderBudgets: { [recommenderId: string]: number },
): RecommenderBudgetPortionSet {
  return {
    portions: recommenderLayerInput.orderedRecommenders.map((recommender) => ({
      id: recommender.id,
      recommender,
      funderIds: [],
      budget: recommenderBudgets[recommender.id] ?? 0,
      standardBudget: standardRecommenderBudgets[recommender.id] ?? 0,
    })),
  };
}
